/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import classNames from 'classnames';
import { useEffect, useId, useRef, useState } from 'react';
import { CmsImage } from '../../CmsImage/CmsImage';
import { MaterialIcon } from '../../MaterialIcon';
import type {
  ProductGridCardPreviewItem,
  RewardToggleColour,
  RewardToggleLabels,
} from '../types';

type RewardToggleProps = {
  items: ProductGridCardPreviewItem[];
  colour?: RewardToggleColour;
  labels: RewardToggleLabels;
  previewImageAlt: string;
  // Optional controlled expansion (lets the parent card lift itself above siblings while open).
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

// Module-level chip colour tokens (CMS-1857): tinted fill + coloured border via DS tokens.
// Blue = brand primary (fill/primary-*), so it follows the brand (SingSaver blue, MoneyHero navy…).
// Yellow = the highlights/yellow pair.
const COLOUR_TOKENS: Record<RewardToggleColour, string> = {
  BLUE: 'bg-fill/primary-light border-fill/primary-dark',
  YELLOW: 'bg-highlights/yellow-light border-highlights/yellow-dark',
};

// Divider between the primary reward and the rest uses the border/accent colour of the selected theme.
const DIVIDER_TOKENS: Record<RewardToggleColour, string> = {
  BLUE: 'bg-fill/primary-dark',
  YELLOW: 'bg-highlights/yellow-dark',
};

const RewardRow = ({
  item,
  previewImageAlt,
  worthLabel,
  showTopup = true,
}: {
  item: ProductGridCardPreviewItem;
  previewImageAlt: string;
  worthLabel: string;
  // The primary chip row shows image + name + worth only; top-up appears on the list rows.
  showTopup?: boolean;
}) => (
  <div className="flex min-h-[38px] items-start gap-spacing/gap/soft-6px">
    {item.imageUrl && (
      <div className="size-[38px] shrink-0">
        <CmsImage
          src={item.imageUrl}
          alt={item.title || previewImageAlt}
          width={38}
          height={38}
          sizes={{ _: 38 }}
          className="size-full object-contain"
        />
      </div>
    )}
    <div className="flex min-w-0 grow flex-col">
      {item.title && (
        <span className="break-words font-bold text-[8px] leading-tight text-text/accent md:text-[12px]">
          {item.title}
        </span>
      )}
      {item.worthValueText && (
        <span className="break-words font-normal text-[8px] leading-tight text-text/accent md:text-[10px]">
          {`${worthLabel} ${item.worthValueText}`}
        </span>
      )}
    </div>
    {showTopup && item.topupText && (
      <span className="shrink-0 self-start font-bold text-[8px] text-buttons/cta">
        {item.topupText}
      </span>
    )}
  </div>
);

export const RewardToggle = ({
  items,
  colour = 'BLUE',
  labels,
  previewImageAlt,
  expanded: expandedProp,
  onExpandedChange,
}: RewardToggleProps) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = expandedProp ?? internalExpanded;
  const panelId = useId();

  // The toggle button renders in a different parent when collapsed vs expanded (below the chip vs
  // inside it), so React remounts the node and focus falls to <body>. Restore focus to the button
  // after the state flips — but only if it had focus (keyboard), so a bare mouse click or an external
  // collapse (single-open) doesn't steal focus.
  const buttonRef = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef(false);

  const toggle = () => {
    const next = !expanded;
    restoreFocus.current = document.activeElement === buttonRef.current;
    if (expandedProp === undefined) {
      setInternalExpanded(next);
    }
    onExpandedChange?.(next);
  };

  useEffect(() => {
    if (restoreFocus.current) {
      buttonRef.current?.focus();
      restoreFocus.current = false;
    }
  }, [expanded]);

  // Escape is handled by the parent grid (single-open), so it collapses without going through
  // toggle() and restoreFocus is never armed — the focused "View less" button then unmounts with
  // nothing to catch focus. Arm the restore in the same keydown when the toggle holds focus.
  useEffect(() => {
    if (!expanded) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        document.activeElement === buttonRef.current
      ) {
        restoreFocus.current = true;
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [expanded]);

  if (!items.length) {
    return null;
  }

  const [primary, ...rest] = items;
  // A single reward has nothing to reveal, so the "View all 1 rewards" toggle and its empty panel
  // are skipped — only the primary chip renders and `expanded` is ignored.
  const hasRest = rest.length > 0;
  const isOpen = hasRest && expanded;

  // Only the affordance is a real button (keyboard + screen-reader focusable, with aria-expanded /
  // aria-controls). The chip and reward rows are non-interactive sibling content so they stay in the
  // accessibility tree (a `role="button"` wrapper would hide them as children-presentational).
  const toggleButton = (
    <button
      ref={buttonRef}
      type="button"
      aria-expanded={isOpen}
      // The controlled panel only exists in the DOM while expanded, so reference it only then
      // (avoids a dangling aria-controls when collapsed).
      aria-controls={isOpen ? panelId : undefined}
      onClick={toggle}
      className="flex items-center justify-center gap-spacing/gap/soft-4px rounded-radius/button font-normal text-[10px] text-text/accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 md:text-[12px]"
    >
      {isOpen ? labels.viewLess : labels.viewAll(items.length)}
      <MaterialIcon
        icon={isOpen ? 'expand_less' : 'expand_more'}
        className="text-[16px] md:text-[18px]"
      />
    </button>
  );

  return (
    <div className="flex flex-col gap-spacing/gap/soft-4px">
      <div
        className={classNames(
          'flex flex-col justify-center gap-spacing/gap/soft-6px rounded-radius/button border p-spacing/padding/soft-8px',
          !isOpen && 'min-h-[50px]',
          COLOUR_TOKENS[colour],
        )}
      >
        <RewardRow
          item={primary}
          previewImageAlt={previewImageAlt}
          worthLabel={labels.worth}
          showTopup={false}
        />

        {isOpen && (
          <>
            <div className={classNames('h-px w-full', DIVIDER_TOKENS[colour])} />
            {toggleButton}
            <div
              id={panelId}
              className="flex flex-col gap-spacing/gap/soft-6px"
            >
              {rest.map((item, index) => (
                <RewardRow
                  key={`${item.imageUrl ?? 'no-image'}-${item.title ?? index}`}
                  item={item}
                  previewImageAlt={previewImageAlt}
                  worthLabel={labels.worth}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {hasRest && !isOpen && toggleButton}
    </div>
  );
};
